import { Swiper } from "swiper11";
import type { CreatedSwiperOpts, CreateSwiperRemoveType, SwiperHost, WrapperOptions } from "@common/types/vendor";
import type { AutoplayOptions, SwiperCustomSwitch, SwiperOptions } from "swiper11/types";
import { padZero } from "./utils";

// Calculate slidesPerView to achieve a target slide width given container width and gap
export function calcSlidesPerView(slideWidth: number, spaceBetween: number, containerWidth: number = 1920): number {
  if (slideWidth <= 0) return 1;
  const n = (containerWidth + spaceBetween) / (slideWidth + spaceBetween);
  return Math.max(1, Number(n.toFixed(2)));
}

export function autoInitSwiper({ page, marker, groupName, pane, controls, swiperOtions }: AutoSwiper): void {
  const PAGE_DOM = $(page);
  const SWIPER_GROUP = PAGE_DOM.find(marker);
  if (!PAGE_DOM.length || !SWIPER_GROUP.length) return;
  const temp: Record<string, Swiper> = {};

  function setControl(control: Record<string, string>, select: string) {
    const config: Record<string, unknown> = {};
    for (const key in control) {
      if (key == "pagination") {
        config[key] = {
          el: select + " " + control[key],
          clickable: swiperOtions?.ClickAble ?? false,
        };
      } else if (key == "navigation") {
        config[key] = {
          nextEl: select + " " + control[key] + "-next",
          prevEl: select + " " + control[key] + "-prev",
        };
      }
    }
    return config;
  }

  SWIPER_GROUP.each(function (i, el) {
    const SWIPER_SELECT = page + " " + marker + "_" + i;
    const SWIPER_CLASS = (marker + "_" + i).replace(".", "");
    $($(el)).addClass(SWIPER_CLASS);
    const hasControls = controls && Object.keys(controls as Record<string, string>).length > 0;
    const res = hasControls ? setControl(controls as Record<string, string>, SWIPER_SELECT) : {};
    temp[SWIPER_CLASS] = new Swiper(SWIPER_SELECT + " .swiper", {
      ...swiperOtions,
      ...res,
    });
    if (pane && pane !== "") {
      const hostEl = temp[SWIPER_CLASS].el as HTMLElement;
      const paneEl = $(hostEl).parents(".tab-pane")[0] as (HTMLElement & { swiperList?: Swiper[] }) | undefined;
      if (paneEl) {
        paneEl.swiperList = paneEl.swiperList || [];
        paneEl.swiperList.push(temp[SWIPER_CLASS]);
      }
    }
  });

  if (!window.swiperGroup || !groupName) return;
  window.swiperGroup[groupName] = {
    ...window.swiperGroup[groupName],
    ...temp,
  };
}
/* 
update By chw 2025-4-17
*/
export function createdSwiper(
  swiperOrList: Swiper | JQuery<HTMLElement>,
  thumbsSelect: string | HTMLElement,
  opts?: CreatedSwiperOpts,
): Promise<Swiper | undefined> {
  const isSwiper = swiperOrList instanceof Swiper;
  const slideList = isSwiper ? $(swiperOrList.slidesEl).find(" > .swiper-slide") : swiperOrList;
  const container: JQuery<HTMLElement> = typeof thumbsSelect === "string" ? $(thumbsSelect) : $(thumbsSelect as HTMLElement); // 缩略图Swiper
  const wrapper = $("<div class='swiper-wrapper'></div>");
  if (!slideList.length || !container.length) return Promise.resolve(undefined);
  const tempFragment = document.createDocumentFragment();
  const { remove = ["fancybox", "zoomsl"], cloneTarget = ".img-box", deepClone = false, appendTo = false, wrapper: rawWrapper = undefined } = opts || {};
  // 标准化为 jQuery 对象（兼容传入字符串/DOM 元素的场景）
  const replaceWrapper = rawWrapper != null ? $(rawWrapper as JQuery<HTMLElement>) : undefined;

  const handleRemove = (type: CreateSwiperRemoveType, target: JQuery<HTMLElement>) => {
    switch (type) {
      case "fancybox":
        $(target).find("a[data-fancybox]").contents().unwrap();
        break;
      case "zoomsl":
        $(target).removeClass("zoomSl")[0];
        break;
      default:
        break;
    }
  };
  const handleReplaceWrapper = (slide: JQuery<HTMLElement>, wrapperTemplate: JQuery<HTMLElement>) => {
    // 每次调用克隆一份新 wrapper，避免多 slide 迭代时复用同一 jQuery 引用
    // 将内容插入模板的最深层叶子节点，支持嵌套模板
    const freshWrapper = wrapperTemplate.clone();
    let leaf: JQuery<HTMLElement> = freshWrapper;
    while (leaf.children().length) {
      leaf = leaf.children().last() as JQuery<HTMLElement>;
    }
    leaf.append(slide.contents());
    return freshWrapper;
  };
  const handleReturnWrapper = () => {
    slideList.each((_i, item) => {
      const target = isSwiper ? $(item).find(cloneTarget) : item;
      let clone = appendTo ? $(target).detach() : $(target).clone(deepClone, deepClone);
      remove.forEach((type) => {
        handleRemove(type, clone);
      });
      if (replaceWrapper) {
        clone = handleReplaceWrapper(clone, replaceWrapper);
      }
      const slide = $(`<div class='swiper-slide'></div>`);
      slide.append(clone);
      tempFragment.appendChild(slide[0]); // Append the slide to the fragment
    });
    wrapper.append(tempFragment);
    container.append(wrapper);
  };

  handleReturnWrapper();

  return new Promise((res, rej) => {
    try {
      const runAfterNextFrame = (cb: () => void) => {
        if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
          window.requestAnimationFrame(cb);
        } else {
          setTimeout(cb, 0);
        }
      };
      let resolved = false;
      const settle = (instance: Swiper) => {
        if (resolved) return;
        resolved = true;
        runAfterNextFrame(() => res(instance));
      };
      const options: SwiperOptions = { ...(opts?.swiperOptions ?? {}) };
      const waitForInit = options.init !== false;
      const originalOn = options.on;
      if (waitForInit) {
        const originalInit = originalOn && typeof originalOn.init === "function" ? originalOn.init : undefined;
        const originalAfterInit = originalOn && typeof originalOn.afterInit === "function" ? originalOn.afterInit : undefined;
        const nextOn = { ...(originalOn ?? {}) };
        nextOn.init = function (this: Swiper, swiper: Swiper) {
          if (originalInit) {
            originalInit.call(this, swiper);
          }
          settle(swiper);
        };
        nextOn.afterInit = function (this: Swiper, swiper: Swiper) {
          if (originalAfterInit) {
            originalAfterInit.call(this, swiper);
          }
          settle(swiper);
        };
        options.on = nextOn;
      } else if (originalOn) {
        options.on = { ...originalOn };
      }

      const createdSwiper = new Swiper(container[0], options);

      if (!waitForInit) {
        settle(createdSwiper);
      }

      if (waitForInit) {
        runAfterNextFrame(() => {
          const host = createdSwiper.el as HTMLElement | undefined;
          if (!resolved && host && host.classList.contains("swiper-initialized")) {
            settle(createdSwiper);
          }
        });
      }

      if (swiperOrList instanceof Swiper) {
        const createType = opts?.type || "thumbs";
        switch (createType) {
          case "controller":
            try {
              createdSwiper.controller.control = swiperOrList;
            } catch (error) {
              console.warn("请为创建的Swiper实例添加‘Controller’模块！！！", error);
            }
            try {
              swiperOrList.controller.control = createdSwiper;
            } catch (error) {
              console.warn("请为主Swiper实例添加‘Controller’模块！！！", error);
            }
            break;
          case "thumbs":
            try {
              swiperOrList.thumbs.swiper = createdSwiper;
            } catch (error) {
              console.warn("请为主Swiper实例添加‘Thumb’模块！！！", error);
              return;
            }
            const initThumbs = (S: Swiper) => {
              S.thumbs.init();
              S.thumbs.update(true);
              S.update();
            };
            // 若 afterInit 已触发（主 Swiper 已完成初始化），直接调用；否则等待事件
            // 场景：在 async beforeInit 中先 await 其他 createdSwiper，导致主 Swiper 已完成 init
            if ((swiperOrList.el as HTMLElement).classList.contains("swiper-initialized")) {
              initThumbs(swiperOrList);
            } else {
              swiperOrList.on("afterInit", initThumbs);
            }
            swiperOrList.on("slideChange", (S) => {
              const thumbs = S.thumbs.swiper;
              const currentIdx = thumbs.params.loop ? S.activeIndex : S.realIndex;
              thumbs.slideTo(currentIdx, 500, false);
            });
            break;
          case "swiper":
            break;
          default:
            break;
        }
      }
    } catch (error) {
      rej(error);
    }
  });
}

export class SwiperVideo {
  private _Swiper: Swiper | undefined = undefined;
  constructor(swiper: Swiper) {
    this._Swiper = swiper;
    if (!this._Swiper) throw Error("swiper can't not be empty.");
    this._addEventListener();
  }
  private _addEventListener() {
    this._Swiper!.on("init", (S) => {
      S.autoplay.stop();
      const videoList = $(S.slides).find("video");
      if (!videoList.length) return;
      videoList.on("ended", () => {
        S.slideNext();
        S.autoplay.start();
      });
    });
    this._Swiper!.on("slideChangeTransitionStart", (S) => {
      $(S.slides).each((_i, item) => {
        const V = this.getVideo(item);
        if (V) V[0].pause();
      });
      const currentSlide = S.slides[S.activeIndex];
      const video = this.getVideo(currentSlide);
      if (video) {
        S.autoplay.stop();
        video[0].play();
      } else {
        S.autoplay.start();
      }
    });
  }
  getVideo(target: HTMLElement | Element) {
    const res = $(target).find("video");
    if (res && res.length) {
      return res;
    } else {
      return false;
    }
  }
}

export const injectToChar = () => {
  Swiper.prototype.toChar = padZero;
};
export const injectSwitchSlide = () => {
  Swiper.prototype.customSwitchSlide = customSwitchSlide;
};

// function customSwitchSlide(swiper: Swiper, activeClassOrConfig?: string | SwiperCustomSwitch, extraConfig?: SwiperCustomSwitch): void {
//   const defaultActiveClass = "custom-active";
//   let activeClass: string;
//   let config: SwiperCustomSwitch;
//   let currentIdx: undefined | number = undefined;
//   const autoplay = swiper.params.autoplay as AutoplayOptions;
//   const delay = autoplay?.delay ?? 3000;
//   const pauseOnMouseEnter = autoplay.pauseOnMouseEnter;
//   let timer: NodeJS.Timeout;
//   if (typeof activeClassOrConfig === "object" && activeClassOrConfig !== null) {
//     config = activeClassOrConfig;
//     activeClass = defaultActiveClass;
//   } else {
//     activeClass = activeClassOrConfig || defaultActiveClass;
//     config = extraConfig || {};
//   }
//   if (swiper.params.loop && swiper.slides.length > Number(swiper.params.slidesPerView)) {
//     swiper.params.slideActiveClass = activeClass;
//     return;
//   } else {
//     swiper.params.loop = false;
//   }
//   //@ts-ignore
//   if (autoplay && autoplay.enabled) {
//     swiper.autoplay.stop();
//   }
//   const startTimer = () => {
//     if (!timer) {
//       timer = setInterval(() => {
//         const currentIdx = swiper.slides.findIndex((i) => $(i).hasClass(activeClass));
//         const nextIdx = currentIdx + 1 === swiper.slides.length ? 0 : currentIdx + 1;
//         switchActive(swiper.slides, nextIdx);
//       }, delay);
//     }
//   };
//   const switchActive = (slides: HTMLElement[], activeIdx: number) => {
//     if (currentIdx === activeIdx) return;
//     currentIdx = activeIdx;
//     $(slides).removeClass(activeClass);
//     $(slides[activeIdx]).addClass(activeClass);
//     swiper.slideTo(activeIdx);
//     if (config.on && config.on.active && typeof config.on.active === "function") {
//       config.on.active(activeIdx, slides[activeIdx], timer);
//     }
//   };
//   switchActive(swiper.slides, swiper.realIndex);
//   swiper.on("click", (s) => {
//     switchActive(s.slides, s.clickedIndex);
//   });
//   startTimer();
//   if (pauseOnMouseEnter) {
//     swiper.el.addEventListener("mouseenter", () => {
//       clearInterval(timer);
//     });
//     swiper.el.addEventListener("mouseleave", () => {
//       startTimer();
//     });
//   }
// }

function customSwitchSlide(swiper: Swiper, activeClassOrConfig?: string | SwiperCustomSwitch, extraConfig?: SwiperCustomSwitch): void {
  const defaultActiveClass = "custom-active";
  let activeClass: string;
  let config: SwiperCustomSwitch;
  let currentIdx: undefined | number = undefined;
  const autoplay = swiper.params.autoplay as AutoplayOptions;
  const delay = autoplay?.delay ?? 3000;
  const pauseOnMouseEnter = autoplay.pauseOnMouseEnter;
  let timer: NodeJS.Timeout;
  if (typeof activeClassOrConfig === "object" && activeClassOrConfig !== null) {
    config = activeClassOrConfig;
    activeClass = defaultActiveClass;
  } else {
    activeClass = activeClassOrConfig || defaultActiveClass;
    config = extraConfig || {};
  }
  if (swiper.params.loop && swiper.slides.length > Number(swiper.params.slidesPerView)) {
    swiper.params.slideActiveClass = activeClass;
    return;
  } else {
    swiper.params.loop = false;
  }
  //@ts-ignore
  if (autoplay && autoplay.enabled) {
    swiper.autoplay.stop();
  }
  const startTimer = () => {
    if (!timer) {
      timer = setInterval(() => {
        const currentIdx = swiper.slides.findIndex((i) => $(i).hasClass(activeClass));
        const nextIdx = currentIdx + 1 === swiper.slides.length ? 0 : currentIdx + 1;
        switchActive(swiper.slides, nextIdx);
      }, delay);
    }
  };
  const switchActive = (slides: HTMLElement[], activeIdx: number) => {
    if (currentIdx === activeIdx) return;
    currentIdx = activeIdx;
    $(slides).removeClass(activeClass);
    $(slides[activeIdx]).addClass(activeClass);
    swiper.slideTo(activeIdx);
    if (config.on && config.on.active && typeof config.on.active === "function") {
      config.on.active(swiper, activeIdx, slides[activeIdx], timer);
    }
  };
  switchActive(swiper.slides, swiper.realIndex);
  swiper.on("click", (s) => {
    switchActive(s.slides, s.clickedIndex);
  });
  startTimer();
  if (pauseOnMouseEnter) {
    swiper.el.addEventListener("mouseenter", () => {
      clearInterval(timer);
    });
    swiper.el.addEventListener("mouseleave", () => {
      startTimer();
    });
  }
}
/**
 * @description: 禁用循环模式，当真实slide数量小于等于可见数量+2时
 * @param {Swiper} swiper Swiper实例
 */
export function disabledLoop(swiper: Swiper): void {
  // const { loop } = swiper.params;
  // const realSlides = swiper.el.querySelectorAll(".swiper-slide");
  // swiper.params.watchSlidesProgress = true;
  // swiper.update();
  // if (loop && realSlides.length && swiper.visibleSlides.length) {
  //   if (realSlides.length <= swiper.visibleSlides.length + 2) {
  //     swiper.params.loop = false;
  //   }
  // }
  const { slidesPerView, loop } = swiper.params;
  const { slides = Array.from(swiper.el.querySelectorAll(".swiper-slide")), loopedSlides = 2 } = swiper;
  const realSlides = slides.length ? slides : swiper.el.querySelectorAll(".swiper-slide");

  if (!loop) return;
  if (
    realSlides.length < Number(slidesPerView!) + Number(loopedSlides) ||
    (swiper.params.effect === "cards" && realSlides.length < Number(slidesPerView) + Number(loopedSlides) * 2)
  ) {
    swiper.params.loop = false;
  }
}

/**
 * @description: 为 Bootstrap tab创建导航Swiper
 * @param {WrapperOptions} wrapperOptions 包含导航容器和导航项选择器等配置
 * @param {CreatedSwiperOpts} createdSwiperOptions 创建Swiper的配置选项
 * @example:
 * createTabNavSwiper(
 *  {
 *    navContainer: selector.find(".swiper-box.thumbs .swiper"),
 *    navItemSelector: selector.find(".tab-content .tab-pane .nav-item"),
 *    paneItemSelector: selector.find(".tab-content .tab-pane"),
 *  },
 *  {
    type: "swiper",
    cloneTarget: ".nav-item",
    deepClone: true,
    swiperOptions: {
      slidesPerView: "auto",
      spaceBetween: 50,
      on: {
        init: (swiper) => {
          $(swiper.slides).find(".nav-item").first().tab("show");
        },
      },
    },
  }
);
 */
export const createTabNavSwiper = async (wrapperOptions: WrapperOptions, createdSwiperOptions: CreatedSwiperOpts) => {
  const { navItemSelector, navContainer, paneItemSelector } = wrapperOptions;
  navItemSelector.on("click", (e) => {
    e.preventDefault();
    const otherItems = navItemSelector.not(e.currentTarget);
    otherItems.removeClass("active");
    $(e.currentTarget).tab("show");
  });
  navItemSelector.one("shown.bs.tab", (e) => {
    const id = $(e.currentTarget).data("target");
    const activePane = paneItemSelector.filter((idx, pane) => {
      return $(pane).attr("id") === id.replace("#", "");
    });
    (activePane.find(".swiper")[0] as SwiperHost)?.swiper?.init();
  });
  return await createdSwiper(navItemSelector, navContainer[0], createdSwiperOptions);
};
